/**
 * **The track events** (`event-engine-cascades` C1): HP reaching 0, a structure or stress point
 * marked, heat going over the cap or landing at 0, and a destroy. One event per point (R3).
 *
 * Each folds as it lands: its "happens" grounds its steps onto the loop's scratch, so the next
 * event's core rules read the track it left. Its core rules (the structure and overheating
 * procedures, Core p.80, p.81) run as its trigger, whole and in RAW's order (R1). Every other
 * listener is deferred to the boundary ({@link EventLoop.boundary}): after the damage or heat that
 * raised it has finished landing.
 */
import type { CombatantId, EncounterState, ResolvedStep } from "@lancer-vtt/domain";
import type { DiceSource } from "../../dice";
import type { LoadedOverlay } from "../effects/loader";
import type { RecordedAnswers } from "../effects/choices";
import type { EventLoop, Happened } from "./loop";

/** HP lands at 0 from above. */
export type HpZeroedEvent = { kind: "hp_zeroed"; combatant: CombatantId };
/** One structure point would be marked; `remaining` is the structure after it. */
export type StructureLostEvent = { kind: "structure_lost"; combatant: CombatantId; remaining: number };
/** One stress point would be marked; `remaining` is the stress after it. */
export type StressLostEvent = { kind: "stress_lost"; combatant: CombatantId; remaining: number };
/** Heat would go over the cap. */
export type HeatExceededEvent = { kind: "heat_exceeded"; combatant: CombatantId };
/** Heat lands at 0 from above. */
export type HeatClearedEvent = { kind: "heat_cleared"; combatant: CombatantId };
/** A destroy step would land. */
export type DestroyedEvent = { kind: "destroyed"; combatant: CombatantId; vaporized?: true };

export type TrackEvent = HpZeroedEvent | StructureLostEvent | StressLostEvent | HeatExceededEvent | HeatClearedEvent | DestroyedEvent;

/**
 * What a cascade runs against: the state it folds from (with the target as the caller sees it),
 * the target, and the intent's loop, overlay and answers. `loop` and `overlay` are required, so a
 * caller that would lose a content fire doesn't compile; a test or forecast with no intent passes a
 * local loop and `SEED_OVERLAY`.
 */
export type CascadeContext = {
  state: EncounterState;
  targetId: CombatantId;
  overlay: LoadedOverlay;
  dice: DiceSource;
  choices?: RecordedAnswers;
  interactive?: boolean;
  loop: EventLoop;
  turnOrdinal: number;
};

/** Named args for {@link raiseTrackEvent}. */
export type RaiseTrackEventArgs = {
  ctx: CascadeContext;
  event: TrackEvent;
  /** The steps the event grounds; they land on `steps` and fold onto the scratch. */
  grounds?: readonly ResolvedStep[];
  /** The record the cascade builds, in order. */
  steps: ResolvedStep[];
  /** The event's core rules, run as it happens is done (R1). */
  core?: (happened: Happened) => void;
  /** Every other listener, delivered at the boundary. */
  listeners?: (happened: Happened) => void;
};

/** Raise `event`: ground its steps, fold them, run its core rules, and defer the rest to the boundary. */
export function raiseTrackEvent(args: RaiseTrackEventArgs): void {
  const { ctx, event, grounds = [], steps, core, listeners } = args;
  ctx.loop.raise(event, {
    happen: () => {
      steps.push(...grounds);
      ctx.loop.land(ctx.state, grounds);
    },
    trigger: (happened) => {
      core?.(happened);
      if (listeners !== undefined) ctx.loop.defer(happened, () => listeners(happened));
    },
  });
}

/** The target as the cascade has folded it so far. */
export function scratchTarget(ctx: CascadeContext) {
  return ctx.loop.scratch(ctx.state).combatants[ctx.targetId]!;
}

/**
 * Raise `heat_cleared` for `combatant` when a vent takes its heat to 0 from `before` (Stabilize's
 * Cool, Shut Down, a cooling `add_heat`; R4). The steps are the caller's; nothing listens yet.
 */
export function raiseHeatCleared(loop: EventLoop | undefined, args: { combatant: CombatantId; before: number; after: number }): void {
  if (loop === undefined || args.before <= 0 || args.after !== 0) return;
  loop.raise({ kind: "heat_cleared", combatant: args.combatant }, { trigger: () => undefined });
}
